import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { FiSave, FiLoader } from 'react-icons/fi';

interface Settings {
  kyc_auto_approve: boolean;
  max_daily_transfer_usd: number;
  max_daily_transfer_lrd: number;
  require_dssn_for_login: boolean;
  maintenance_mode: boolean;
}

const API_URL = process.env.REACT_APP_API_URL || 'https://api.liberianpost.com';

const Settings: React.FC = () => {
  const { employee } = useAuth();
  const [settings, setSettings] = useState<Settings>({
    kyc_auto_approve: false,
    max_daily_transfer_usd: 1000,
    max_daily_transfer_lrd: 100000,
    require_dssn_for_login: true,
    maintenance_mode: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/employees/settings`);
      setSettings(response.data.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await axios.put(`${API_URL}/employees/settings`, settings);
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <main className="main-content" style={{ marginLeft: 0 }}>
          <div className="loading">Loading settings...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <main className="main-content" style={{ marginLeft: 0 }}>
        <div className="page-header">
          <h1>System Settings</h1>
          <p>Configure system-wide parameters</p>
        </div>

        {message && (
          <div className={`settings-message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="settings-section glass">
          <h2>KYC Settings</h2>
          <div className="setting-item">
            <div className="setting-info">
              <label>Auto-approve KYC</label>
              <p>Automatically approve KYC submissions without manual review</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.kyc_auto_approve}
                onChange={(e) => setSettings({ ...settings, kyc_auto_approve: e.target.checked })}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        <div className="settings-section glass">
          <h2>Transaction Limits</h2>
          <div className="setting-item">
            <div className="setting-info">
              <label>Max Daily Transfer (USD)</label>
              <p>Maximum amount a user can transfer per day in USD</p>
            </div>
            <input
              type="number"
              className="settings-input"
              value={settings.max_daily_transfer_usd}
              onChange={(e) => setSettings({ ...settings, max_daily_transfer_usd: parseFloat(e.target.value) })}
            />
          </div>
          <div className="setting-item">
            <div className="setting-info">
              <label>Max Daily Transfer (LRD)</label>
              <p>Maximum amount a user can transfer per day in LRD</p>
            </div>
            <input
              type="number"
              className="settings-input"
              value={settings.max_daily_transfer_lrd}
              onChange={(e) => setSettings({ ...settings, max_daily_transfer_lrd: parseFloat(e.target.value) })}
            />
          </div>
        </div>

        <div className="settings-section glass">
          <h2>Security Settings</h2>
          <div className="setting-item">
            <div className="setting-info">
              <label>Require DSSN for Login</label>
              <p>Users must enter their DSSN during login</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.require_dssn_for_login}
                onChange={(e) => setSettings({ ...settings, require_dssn_for_login: e.target.checked })}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        <div className="settings-section glass">
          <h2>Maintenance</h2>
          <div className="setting-item">
            <div className="setting-info">
              <label>Maintenance Mode</label>
              <p>Put the system in maintenance mode (only admins can access)</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.maintenance_mode}
                onChange={(e) => setSettings({ ...settings, maintenance_mode: e.target.checked })}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        <div className="settings-actions">
          <button className="save-btn" onClick={handleSave} disabled={saving}>
            {saving ? <FiLoader className="spinning" /> : <FiSave />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </main>
    </div>
  );
};

export default Settings;
